/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { GeoJsonLayer, IconLayer } from '@deck.gl/layers';
import { RGBAColor, Position as DeckGlPosition } from '@deck.gl/core';
import { GeoJSON, Position } from '../../../services/api';
import {
  FLOORPLAN_ZOOM,
  getCentreOfObject,
  getFloorPlanItemElevation,
  getFloorPlanItemFillColour,
  getFloorPlanItemLineColour,
  ICON_ATLAS,
  ICON_MAPPING,
  LocationStateProps,
} from '../mapHelpers';
import { SensorPositionData } from '../types';
import getTargetPosLayer, { TargetPosIconType } from './targetPosLayer';

const getPosFloorLayers = (
  floorPlan: GeoJSON | undefined,
  hoveredPolygon: string,
  setHoveredPolygon: React.Dispatch<React.SetStateAction<string>>,
  onChange: (newPosition: SensorPositionData) => void,
  position: Position | undefined,
  mapLocationState: LocationStateProps,
  gatewayPosition: Position | undefined,
  userPosition: Position | null,
  userLocation: string,
  currentLocation: string
) => {
  const deckGLLayers = [];

  const getFloorPlanLayer = () => {
    if (floorPlan && floorPlan.features.length > 0) {
      return new GeoJsonLayer({
        id: 'geojson-layer',
        // @ts-ignore
        data: floorPlan,
        pickable: true,
        stroked: true,
        filled: true,
        wireframe: true,
        extruded: true,
        onClick: (obj: any) => {
          if (obj?.picked) {
            const objData = obj.object;
            let coords: DeckGlPosition;
            let polygonId = objData.id ?? objData.properties?.id;
            switch (objData?.properties?.type) {
              case 'area.room':
              case 'area.toilet':
              case 'area.toilet.m':
              case 'area.toilet.w':
              case 'area.toilet.d':
                coords = obj.coordinate;
                break;
              case 'furniture.desk':
                coords = getCentreOfObject(objData.geometry, floorPlan, polygonId);
                break;
              default:
                coords = obj.coordinate;
                polygonId = '';
                break;
            }

            if (coords) {
              onChange({
                ...position,
                lng: Math.round(coords[0] * 1e7) / 1e7,
                lat: Math.round(coords[1] * 1e7) / 1e7,
                polygon: polygonId.toString(),
              });
            }
          }
        },
        onHover: (obj: any) => {
          let polygonId = '';

          if (obj?.picked) {
            const objData = obj.object;
            switch (objData?.properties?.type) {
              case 'area.room':
              case 'furniture.desk':
              case 'area.toilet':
              case 'area.toilet.m':
              case 'area.toilet.w':
              case 'area.toilet.d':
                polygonId = objData.id ?? objData.properties?.id;
                break;
              default:
                break;
            }
          }

          setHoveredPolygon(polygonId);
        },
        getFillColor: (d: any): RGBAColor => {
          const polygonId: string | number = d.id ?? d.properties?.id;

          if (position?.polygon) {
            if (polygonId?.toString() === position.polygon) {
              return [52, 95, 145, 164];
            }
          }

          if (hoveredPolygon) {
            if (polygonId === hoveredPolygon) {
              return [52, 95, 145, 200];
            }
          }

          return getFloorPlanItemFillColour(d?.properties?.type ?? '');
        },
        getLineColor: (d: any): RGBAColor => getFloorPlanItemLineColour(d?.properties?.type ?? ''),
        getElevation: (d: any) => getFloorPlanItemElevation(d?.properties?.type ?? ''),
        lineWidthMinPixels: 2,
        lineWidthMaxPixels: 2,
        updateTriggers: {
          getFillColor: [hoveredPolygon, position?.polygon],
          getLineColor: [hoveredPolygon],
        },
      });
    }

    return null;
  };

  const getLabelLayer = () => {
    if (floorPlan && floorPlan.features.length > 0 && mapLocationState.zoom >= FLOORPLAN_ZOOM) {
      const labelData = floorPlan.features.filter(({ geometry, properties }) => {
        const coordinates = geometry?.coordinates;
        const polygonType = properties?.type;
        if (coordinates) {
          switch (polygonType) {
            case 'area.toilet.m':
            case 'area.toilet.w':
            case 'area.toilet.d':
            case 'area.toilet':
            case 'area.lift':
            case 'area.stairs':
              return true;
            default:
              return false;
          }
        }
        return false;
      });
      if (labelData.length > 0) {
        return new IconLayer({
          id: 'label-layer',
          data: labelData,
          pickable: false,
          // iconAtlas and iconMapping are required
          // getIcon: return a string
          iconAtlas: ICON_ATLAS,
          iconMapping: ICON_MAPPING,
          // @ts-ignore
          getIcon: (d: any) => {
            switch (d?.properties?.type) {
              case 'area.toilet.m':
                return 'toilet.m';
              case 'area.toilet.w':
                return 'toilet.f';
              case 'area.toilet.d':
                return 'toilet.d';
              case 'area.toilet':
                return 'toilet';
              case 'area.lift':
                return 'lift';
              case 'area.stairs':
                return 'stairs';
              default:
                return 'unknown';
            }
          },
          sizeScale: 3.5,
          getPosition: ({ geometry }: any) => getCentreOfObject(geometry),
          getSize: (d: any) => {
            switch (d?.properties?.type) {
              case 'area.toilet.m':
              case 'area.toilet.w':
              case 'area.toilet.d':
              case 'area.lift':
              case 'area.stairs':
              default:
                return 5;
            }
          },
          getColor: () => [255, 255, 255, 175],
        });
      }
    }

    return null;
  };

  const floorPlanLayer = getFloorPlanLayer();

  if (floorPlanLayer !== null) {
    deckGLLayers.push(floorPlanLayer);
  }

  const labelLayer = getLabelLayer();
  if (labelLayer !== null) {
    deckGLLayers.push(labelLayer);
  }

  if (position) {
    deckGLLayers.push(
      new IconLayer({
        id: 'marker-layer',
        data: [{ data: 0, position: [position.lng, position.lat, position.height ?? 0] }],
        pickable: true,
        iconAtlas: ICON_ATLAS,
        iconMapping: ICON_MAPPING,
        getAngle: () => (typeof position.azimuth === 'number' ? -position.azimuth : 0),
        getIcon: () => (typeof position.azimuth === 'number' ? 'sensor' : 'marker'),
        sizeScale: 15,
        // @ts-ignore
        getPosition: (d: Position) => d.position,
        getSize: 4,
        getColor: () => [52, 95, 145, 255], // is always default to blue
        updateTriggers: {
          getIcon: [position.azimuth],
          getAngle: [position.azimuth],
        },
        billboard: false,
      })
    );
  }

  const getGatewayPosLayer = () => {
    let dataLayer = null;
    if (gatewayPosition) {
      dataLayer = getTargetPosLayer(gatewayPosition, TargetPosIconType.sensor);
    }
    return dataLayer;
  };

  const getUserPosLayer = () => {
    let dataLayer = null;
    if (userPosition && userLocation === currentLocation) {
      dataLayer = getTargetPosLayer(userPosition);
    }
    return dataLayer;
  };

  const userPosLayer = getUserPosLayer();
  if (userPosLayer !== null) {
    deckGLLayers.push(userPosLayer);
  }

  const gatewayPosLayer = getGatewayPosLayer();
  if (gatewayPosLayer !== null) {
    deckGLLayers.push(gatewayPosLayer);
  }

  return deckGLLayers;
};

export default getPosFloorLayers;
